import React, { useState, useEffect } from "react";
import { Minus, Plus } from "lucide-react";
import { EDSButton } from "./EDSButton";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";

export type EDSQuantityStepperSize = "sm" | "md" | "lg";

interface EDSQuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  className?: string;
  size?: EDSQuantityStepperSize;
  inputId?: string;
}

export const EDSQuantityStepper: React.FC<EDSQuantityStepperProps> = ({
  value,
  onChange,
  min = 1,
  max = 999,
  step = 1,
  disabled = false,
  className,
  size = "md",
  inputId
}) => {
  const [inputValue, setInputValue] = useState(value.toString());

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const handleDecrease = () => {
    const newValue = Math.max(min, value - step);
    if (newValue !== value) {
      onChange(newValue);
    }
  };

  const handleIncrease = () => {
    const newValue = Math.min(max, value + step);
    if (newValue !== value) {
      onChange(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    setInputValue(inputVal);

    // Allow empty input temporarily for better UX
    if (inputVal === "") {
      return;
    }

    const numValue = parseInt(inputVal, 10);
    if (!isNaN(numValue) && numValue >= min && numValue <= max) {
      onChange(numValue);
    }
  };

  const handleInputBlur = () => {
    // Reset to current value if input is invalid
    const numValue = parseInt(inputValue, 10);
    if (isNaN(numValue) || numValue < min || numValue > max) {
      setInputValue(value.toString());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      handleIncrease();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      handleDecrease();
    }
  };

  const sizeClasses = {
    sm: "h-8",
    md: "h-9",
    lg: "h-10"
  };

  const getButtonSize = (
    size: EDSQuantityStepperSize
  ): EDSQuantityStepperSize => {
    if (size === "sm") return "sm";
    if (size === "lg") return "lg";
    return "md";
  };

  const buttonSize = getButtonSize(size);

  return (
    <div
      className={cn(
        "eds-quantity-stepper inline-flex items-center border border-border rounded-md bg-background overflow-visible relative",
        sizeClasses[size],
        className
      )}
    >
      {/* Minus Button */}
      <EDSButton
        variant="ghost"
        size={buttonSize}
        onClick={handleDecrease}
        disabled={disabled || value <= min}
        className="rounded-r-none border-0 border-r border-border h-full px-2 relative z-[1] transition-all duration-200 ease-in-out hover:!bg-accent hover:!text-accent-foreground focus-visible:outline-2 focus-visible:outline-[var(--ring,#277b7b)] focus-visible:outline-offset-2 focus-visible:z-[3]"
        aria-label="Decrease quantity"
      >
        <Minus className="h-4 w-4" />
      </EDSButton>

      {/* Input Field */}
      <Input
        id={inputId}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          "border-0 border-l border-r border-border rounded-none text-center font-medium",
          "shadow-none outline-none relative z-[1]",
          "focus:outline-2 focus:outline-[var(--ring,#277b7b)] focus:outline-offset-2 focus:shadow-none focus:z-[2]",
          "min-w-0 flex-shrink-0",
          size === "sm" && "w-10",
          size !== "sm" && "w-16",
          size === "sm" && "h-8 text-sm",
          size === "md" && "h-9 text-sm",
          size === "lg" && "h-10 text-base"
        )}
        aria-label="Quantity"
      />

      {/* Plus Button */}
      <EDSButton
        variant="ghost"
        size={buttonSize}
        onClick={handleIncrease}
        disabled={disabled || value >= max}
        className="rounded-l-none border-0 h-full px-2 relative z-[1] transition-all duration-200 ease-in-out hover:!bg-accent hover:!text-accent-foreground focus-visible:outline-2 focus-visible:outline-[var(--ring,#277b7b)] focus-visible:outline-offset-2 focus-visible:z-[3]"
        aria-label="Increase quantity"
      >
        <Plus className="h-4 w-4" />
      </EDSButton>
    </div>
  );
};
